import { morphSection } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * Eagle Ray — cart tier discount codes.
 *
 * The cart progress bar promises rewards per tier (e.g. 2 items → ₹500 off,
 * 3 items → ₹1000 off). Each tier can name a discount code (theme settings →
 * progress_tier_N_discount_code; config rendered by
 * snippets/eagleray-tier-discount.liquid). This keeps exactly the code of the
 * highest tier reached on the cart: it is added when a tier is reached, swapped
 * when the tier changes and removed when the cart drops below every tier.
 * Codes the shopper entered themselves are left alone.
 *
 * After changing the codes, the cart sections (drawer / cart page) are
 * re-rendered in place so Savings and Subtotal include the discount. This is
 * deliberately not done through a cart:update event: with the drawer's
 * auto-open setting, that event opens the drawer.
 *
 * This file must stay an ES module with static imports. Shopify minifies theme
 * assets on the CDN, and a dynamic import() inside a classic script gets
 * rewritten to require(), which does not exist in the browser.
 */

const configEl = document.getElementById('eagleray-tier-discount-config');
const config = configEl ? JSON.parse(configEl.textContent) : null;

let busy = false;
let again = false;

/** @returns {string | null} Code of the highest tier the cart has reached. */
function wantedCode(cart) {
  const current = config.mode === 'spend' ? cart.original_total_price : cart.item_count;
  let best = null;
  for (const tier of config.tiers) {
    const goal = config.mode === 'spend' ? tier.target * 100 : tier.target;
    if (current >= goal && (!best || goal > best.goal)) best = { goal, code: tier.code };
  }
  return best ? best.code : null;
}

/** @param {string[]} list */
const normalize = (list) => list.map((code) => code.toLowerCase()).sort().join(',');

async function sync() {
  if (busy) {
    again = true;
    return;
  }
  busy = true;

  try {
    // Read the cart fresh: this runs right after other cart writes.
    const cart = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' }).then((r) => r.json());

    const tierCodes = config.tiers.map((tier) => tier.code.toLowerCase());
    const existing = cart.discount_codes.map((/** @type {{ code: string }} */ d) => d.code);
    const next = existing.filter((code) => !tierCodes.includes(code.toLowerCase()));
    const wanted = wantedCode(cart);
    if (wanted) next.push(wanted);

    if (normalize(next) === normalize(existing)) return;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: next.join(','), sections: sectionIds }),
    }).then((r) => r.json());

    for (const id of sectionIds) {
      const html = response.sections?.[id];
      if (html) await morphSection(id, html);
    }
    if (sectionIds.length) document.dispatchEvent(new CustomEvent('eagleray:cart-rendered'));
  } catch (error) {
    console.error('[eagleray-tier-discount]', error);
  } finally {
    busy = false;
    if (again) {
      again = false;
      sync();
    }
  }
}

if (config && config.tiers.length) {
  document.addEventListener(ThemeEvents.cartUpdate, () => sync());
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) sync();
  });
  sync();
}
